// @flow

import {
  ACCEPT_INVITE,
  ACCEPT_INVITE_DONE,
  ACCEPT_INVITE_ERROR,
  ACCEPT_TERMS,
  ACCEPT_TERMS_DONE,
  ACCEPT_TERMS_ERROR,
  CONFIRMATION_CODE,
  CONFIRMATION_CODE_DONE,
  CONFIRMATION_CODE_ERROR,
  FORCE_MFA,
  LOG_IN,
  LOG_IN_ERROR,
  LOG_IN_MFA_ERROR,
  LOG_IN_SUCCESS,
  LOG_OUT,
  REJECT_WITHINGS_INTEGRATION,
  RESET_LOADING_USER,
  RESET_LOG_IN_ERROR,
  RESET_LOG_IN_MFA_ERROR,
  SET_CREDENTIALS,
  SET_CREDENTIALS_DONE,
  SET_CREDENTIALS_ERROR,
  SET_FACILITY_DETAILS,
  SET_USER_NAME,
  SET_WEBEX_FOR_STAFF,
  TOKEN_REFRESH_SUCCESS,
  UPDATE_AVAILABILITY_STATUS,
  UPDATE_PREFERRED_LOCALE,
  UPDATE_SPACE_INFO,
  UPDATE_TWO_FA_STATUS
} from "../actions/actionTypes";

import { REHYDRATE } from "redux-persist/es/constants";
import UserService from "../../services/userService";
import masterDataService from "../../services/masterDataService";
import statusesService from "../../services/statusesService";
import patientTypesService from "../../services/patientTypesService";
import globalCacheService from "../../services/globalCacheService";
import { devLog } from "../../utils/devLog";

const initialState = {
  loading: false,
  loggedIn: false,
  loginError: false,
  role: "",
  data: {},
  spaceDetails: {},
  onboarding: {
    inviteAccepted: false,
    termsAccepted: false,
    privacyAccepted: false,
    confirmationDone: false,
    credentialsSet: false,
    error: false,
    step: "invite",
    inviteCode: "",
    email: "",
    phone: "",
    userName: ""
  }
};

const userReducer = (state = initialState, action) => {
  switch (action.type) {
    case RESET_LOADING_USER:
      return {
        ...initialState,
        role: state.role,
        spaceDetails: { ...state.spaceDetails },
        loading: false,
        onboarding: {
          inviteAccepted: false,
          termsAccepted: false,
          confirmationDone: false,
          credentialsSet: false,
          error: false,
          errorMsg: "",
          step: "invite",
          inviteCode: "",
          email: "",
          phone: ""
        }
      };

    case ACCEPT_INVITE:
    case ACCEPT_TERMS:
    case CONFIRMATION_CODE:
    case SET_CREDENTIALS:
    case LOG_IN:
      return { ...state, loading: true };

    case ACCEPT_INVITE_DONE:
      const { email, phone, inviteCode } = action;
      return {
        ...state,
        loading: false,
        onboarding: {
          ...state.onboarding,
          inviteAccepted: true,
          error: false,
          email,
          phone,
          inviteCode
        },
        spaceDetails: action.data
      };

    case ACCEPT_TERMS_DONE:
      return {
        ...state,
        loading: false,
        onboarding: {
          ...state.onboarding,
          termsAccepted: true,
          error: false
        }
      };

    case CONFIRMATION_CODE_DONE:
      return {
        ...state,
        loading: false,
        onboarding: {
          ...state.onboarding,
          confirmationDone: true,
          error: false
        },
        spaceDetails: action.data
      };

    case SET_CREDENTIALS_DONE:
      return {
        ...state,
        loading: false,
        onboarding: {
          ...state.onboarding,
          credentialsSet: true,
          error: false
        }
      };

    case SET_USER_NAME:
      return {
        ...state,
        onboarding: {
          ...state.onboarding,
          userName: action.userName
        }
      };

    case ACCEPT_INVITE_ERROR:
    case ACCEPT_TERMS_ERROR:
    case CONFIRMATION_CODE_ERROR:
    case SET_CREDENTIALS_ERROR:
      return {
        ...state,
        onboarding: {
          ...state.onboarding,
          error: true,
          errorMsg: action.error
        },
        loading: false
      };

    case UPDATE_SPACE_INFO:
      // Strip out attributeUIFields to prevent bloating user state (it's stored separately in global storage)
      const { attributeUIFields, ...dataWithoutUIFields } = action.data;
      return {
        ...state,
        data: {
          ...action.data,
          ...dataWithoutUIFields,
          preferredLocale: state.data.preferredLocale
        },
        role: action.data.role,
        provisionedFeatures: action.data.provisionedFeatures
      };

    case LOG_IN_SUCCESS:
      return {
        ...state,
        loading: false,
        loggedIn: true,
        data: action.data,
        token: action.token,
        refreshToken: action.refreshToken,
        expiresIn: action.expiresIn,
        tokenTimestamp: action.tokenTimestamp,
        role: action.data.role,
        provisionedFeatures: action.data.provisionedFeatures,
        logoutMessage: "",
        mfaRequired: false,
        mfaToken: null,
        mfaMechanism: null,
      };

    case TOKEN_REFRESH_SUCCESS:
      return {
        ...state,
        token: action.token,
        refreshToken: action.refreshToken,
        expiresIn: action.expiresIn,
        tokenTimestamp: Date.now(),
      };

    case SET_FACILITY_DETAILS:
      return {
        ...state,
        facility: action.facility
      };

    case UPDATE_AVAILABILITY_STATUS:
      let data = { ...state.data };
      data.availabilityStatus = action.availabilityStatus;
      return { ...state, data: data };

    case UPDATE_PREFERRED_LOCALE:
      // locale: state.user.data.preferredLocale
      return {
        ...state,
        data: { ...state.data, preferredLocale: action.data.preferredLocale }
      };

    case UPDATE_TWO_FA_STATUS:
      return {
        ...state,
        data: { ...state.data, twoFAStatus: action.data.twoFAStatus }
      };

    case REJECT_WITHINGS_INTEGRATION:
      return {
        ...state,
        data: {
          ...state.data,
          rejectedWithingsIntegration: true
        }
      };

    case LOG_IN_ERROR:
      return {
        ...state,
        loading: false,
        loggedIn: false,
        loginError: true,
        mfaRequired: false,
        errorMessage: action.errorMessage
      };

    case RESET_LOG_IN_ERROR:
      // console.log('LOG IN ERROR');
      return {
        ...state,
        loading: false,
        loggedIn: false,
        loginError: false,
        mfaRequired: false,
        errorMessage: ""
      };

    case RESET_LOG_IN_MFA_ERROR:
      return {
        ...state,
        loading: false,
        loggedIn: false,
        loginError: false,
        errorMessage: ""
      };

    case LOG_IN_MFA_ERROR:
      // console.log('LOG IN ERROR');
      return {
        ...state,
        loading: false,
        loggedIn: false,
        loginError: true,
        errorMessage: action.errorMessage
      };

    case FORCE_MFA:
      // console.log('LOG IN ERROR');
      return {
        ...state,
        loading: false,
        mfaRequired: true,
        mfaToken: action.token,
        mfaMechanism: action.mechanism
      };

    case SET_WEBEX_FOR_STAFF:
      return { ...state, webex: action.webex };

    case LOG_OUT:
      UserService.removeToken();
      masterDataService.clearCache();
      statusesService.clearTemplatesCache();
      patientTypesService.clearTopicsCache();
      // Every LOG_OUT lands here, including the 401 handler, a token refresh
      // failure and the channel hand-over, none of which run onLogout()'s
      // clears. The next loginFlow reads getSpaceInfo() through this cache, so
      // a surviving entry would hand the new session the previous user's space.
      globalCacheService.clearAllCache();
      return { ...initialState, logoutMessage: action.message };
    case REHYDRATE:
      // devLog('REHYDRATE', action.payload, process.env);
      if (action.payload && action.payload.user) {
        if (action.payload.user.loggedIn && action.payload.user.token) {
          devLog("TOKEN ", action.payload.user.token);
          // if logged in and has token setToken
          // updateToken will call getSpaceInfo which fetches fresh data from API
          // Also pass refresh token info for automatic token refresh
          // Use setTimeout to defer this call until after the reducer completes,
          // since updateToken may call store.getState() (via tokenRefreshService)
          setTimeout(() => {
            UserService.updateToken(
              action.payload.user.token,
              action.payload.user.refreshToken,
              action.payload.user.expiresIn,
              action.payload.user.tokenTimestamp
            );
          }, 0);
        } else if (
          action.payload.user.onboarding &&
          action.payload.user.onboarding.inviteAccepted
        ) {
          // do not destroy
        } else {
          // not loggedIn or doesn't have a token
          action.payload.user = initialState;
        }

        // Safely extract role and provisionedFeatures with fallbacks
        // If data is missing/incomplete, getSpaceInfo (called by updateToken above) will refresh it
        const rehydratedData = action.payload.user.data || {};
        const role = rehydratedData.role || state.role || "";
        const provisionedFeatures = rehydratedData.provisionedFeatures || state.provisionedFeatures || action.payload.user.provisionedFeatures;

        return {
          ...state,
          ...action.payload.user,
          loading: false,
          role,
          provisionedFeatures
        };
      }
      return state;

    default:
      return state;
  }
};

export default userReducer;
